import React, { useState } from "react";
import {
  FolderSidebarMenuButton,
  FolderSidebarMenuSubButton,
} from "./FolderSidebarComponents";
import IconProvider from "../../../../ui/IconProvider";
import ContextMenu, { ContextMenuItem } from "../../../../ui/ContextMenu";
import { FolderItem, SelectedEntity } from "./FolderStructure";
import { cn } from "@/lib/utils";
import { useRTL } from "@/providers/RTLProvider";
import { FolderColorPickerDialog } from "../../../../ui/dialogs/FolderColorPickerDialog";
import { getItemTypeIcon, getItemTypeLabel } from "@/lib/config/pageItemConfig";

interface FolderMenuItemProps {
  folder: FolderItem;
  level: number;
  selectedFolderId?: string | null;
  onFolderSelect: (folderId: string | null) => void;
  onFolderCreate?: (parentId?: string) => void;
  onFolderRename?: (folderId: string, newName: string) => void;
  onFolderDelete?: (folderId: string) => void;
  onFolderColorChange?: (folderId: string, color: string | null) => void;
  onItemDrop?: (itemId: string, folderId: string | null) => void;
  onFolderMove?: (folderId: string, newParentId: string | null) => void;
  showItemCounts?: boolean;
  allowCreation?: boolean;
  allowRename?: boolean;
  allowDelete?: boolean;
  enableDragDrop?: boolean;
  isExpanded?: boolean;
  onToggleExpanded?: () => void;
  chevronSize?: number;
  showFolderIcon?: boolean;
  folderIconSize?: number;
  // Item handling props
  showItems?: boolean;
  onItemSelect?: (item: any) => void;
  selectedItemId?: string | null;
  // Item type and creation props
  itemType?: 'glossary' | 'document' | 'blueprint' | 'dialog' | 'flow' | 'variable';
  oncreateObject?: (folderId?: string) => void;
  onCreateDocument?: (folderId?: string) => void;
  onCreateBlueprint?: (folderId?: string) => void;
  onCreateDialog?: (folderId?: string) => void;
  onCreateFlow?: (folderId?: string) => void;
  onCreateVariable?: (folderId?: string) => void;
  // Item actions
  onItemRename?: (itemId: string, newName: string) => void;
  onItemDelete?: (itemId: string) => void;
  onItemDuplicate?: (itemId: string) => void;
  // Multi-select props
  selectedEntities?: Set<string>;
  onEntityClick?: (entity: SelectedEntity, event?: React.MouseEvent) => void;
  onBatchMove?: (targetFolderId: string | null) => void;
  // Auto-edit props
  newlyCreatedFolderId?: string | null;
  onClearNewlyCreatedFolder?: () => void;
}

export default function FolderMenuItem({
  folder,
  level,
  selectedFolderId,
  onFolderSelect,
  onFolderCreate,
  onFolderRename,
  onFolderDelete,
  onFolderColorChange,
  onItemDrop,
  onFolderMove,
  showItemCounts = true,
  allowCreation = true,
  allowRename = true,
  allowDelete = true,
  enableDragDrop = true,
  isExpanded = false,
  onToggleExpanded,
  chevronSize = 4,
  showFolderIcon = false,
  folderIconSize = 14,
  // Item handling props
  showItems = false,
  onItemSelect,
  selectedItemId,
  // Item type and creation props
  itemType = 'glossary',
  oncreateObject,
  onCreateDocument,
  onCreateBlueprint,
  onCreateDialog,
  onCreateFlow,
  onCreateVariable,
  // Item actions
  onItemRename,
  onItemDelete,
  onItemDuplicate,
  // Multi-select props
  selectedEntities = new Set(),
  onEntityClick,
  onBatchMove,
  // Auto-edit props
  newlyCreatedFolderId,
  onClearNewlyCreatedFolder,
}: FolderMenuItemProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editingName, setEditingName] = useState(folder.name);
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState<{
    isOpen: boolean;
    position: { x: number; y: number };
  }>({
    isOpen: false,
    position: { x: 0, y: 0 },
  });
  const inputRef = React.useRef<HTMLInputElement>(null);
  
  // Determine if this is an item or folder
  const isItem = folder.type === 'item';
  const isFolder = !isItem;
  
  // Auto-edit newly created folders
  React.useEffect(() => {
    if (isFolder && newlyCreatedFolderId && folder.id === newlyCreatedFolderId) {
      setIsEditing(true);
      setEditingName(folder.name);
      // Focus the input after a brief delay to ensure it's rendered
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 50);
      if (onClearNewlyCreatedFolder) {
        onClearNewlyCreatedFolder();
      }
    }
  }, [isFolder, newlyCreatedFolderId, folder.id, folder.name, onClearNewlyCreatedFolder]);
  
  // Check if this entity is part of multi-select
  // For items, check using the actual item ID, not the wrapper ID
  const entityId = isItem && folder.itemData?.id ? folder.itemData.id : folder.id;
  const isMultiSelected = selectedEntities.has(entityId);
  
  // Selection logic
  // For "Root" folder: selected when selectedFolderId is null (showing all items)
  const isSelected = isFolder 
    ? (folder.id === 'root' ? selectedFolderId === null : selectedFolderId === folder.id)
    : selectedItemId === folder.id;
  
  // Debug logging for selection state
  React.useEffect(() => {
    if (folder.id === 'root' || folder.id === '__ALL_ITEMS__') {
      console.log(`FolderMenuItem ${folder.name} (${folder.id}): selectedFolderId=${selectedFolderId}, isSelected=${isSelected}`);
    }
  }, [selectedFolderId, isSelected, folder.id, folder.name]);
  const hasChildren = folder.children && folder.children.length > 0;
  const isDraggedOver = dragOverFolderId === folder.id;
  const isSubItem = level > 0;
  const { isRTL } = useRTL(); // Get RTL mode for chevron direction

  const startEditing = () => {
    setIsEditing(true);
    setEditingName(folder.name);
  };

  const saveEdit = () => {
    if (editingName.trim()) {
      if (isItem && onItemRename && folder.itemData?.id) {
        // Rename item
        onItemRename(folder.itemData.id, editingName.trim());
      } else if (isFolder && onFolderRename) {
        // Rename folder
        onFolderRename(folder.id, editingName.trim());
      }
    }
    setIsEditing(false);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditingName(folder.name);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      saveEdit();
    } else if (e.key === "Escape") {
      cancelEdit();
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (!enableDragDrop || !onItemDrop) return;
    
    // Don't allow dropping on items, only on folders
    if (isItem) return;

    e.preventDefault();
    e.stopPropagation();
    setDragOverFolderId(folder.id);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!enableDragDrop || !onItemDrop) return;

    e.preventDefault();
    e.stopPropagation();
    setDragOverFolderId(null);
  };

  const handleDragStart = (e: React.DragEvent) => {
    if (!enableDragDrop) return;

    // Prevent dragging the root folder itself
    if (folder.id === 'root' && isFolder) {
      console.log('Cannot drag root folder');
      e.preventDefault();
      return;
    }

    console.log('FolderMenuItem handleDragStart', {
      entityId,
      selectedEntities: Array.from(selectedEntities),
      isMultiSelected,
      selectedCount: selectedEntities.size
    });

    // Check if we're dragging multiple selected items
    if (selectedEntities.size > 1 && isMultiSelected) {
      // Dragging multiple items
      const dragData = {
        isMultiSelect: true,
        count: selectedEntities.size,
        entities: Array.from(selectedEntities)
      };
      console.log('Setting multi-select drag data:', dragData);
      e.dataTransfer.setData("application/json", JSON.stringify(dragData));
      e.dataTransfer.effectAllowed = "move";
      
      // Create custom drag image showing count
      if (e.currentTarget instanceof HTMLElement) {
        const dragImage = document.createElement('div');
        dragImage.className = 'bg-sidebar-accent text-sidebar-accent-foreground px-3 py-2 rounded shadow-lg border border-sidebar-border';
        dragImage.innerHTML = `
          <div class="flex items-center gap-2">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"/>
            </svg>
            <span class="font-medium">${selectedEntities.size} items</span>
          </div>
        `;
        dragImage.style.position = 'absolute';
        dragImage.style.top = '-1000px';
        document.body.appendChild(dragImage);
        e.dataTransfer.setDragImage(dragImage, 0, 0);
        
        // Clean up after a brief delay
        setTimeout(() => document.body.removeChild(dragImage), 0);
      }
    } else {
      // Set drag data based on whether this is a folder or item
      if (isItem && folder.itemData) {
        // Dragging a single item - use the actual item ID
        const itemData = {
          type: 'item',
          id: folder.itemData.id, // Use actual item ID, not wrapper ID
          itemType: itemType,
          sourceFolder: folder.parentId
        };
        e.dataTransfer.setData("application/json", JSON.stringify(itemData));
        
        // Also set React Flow format for Logic page
        e.dataTransfer.setData("application/reactflow", JSON.stringify({
          itemId: folder.itemData.id,
          itemType: itemType,
          label: folder.name,
        }));
        
        e.dataTransfer.effectAllowed = "move";
      } else if (isFolder) {
        // Dragging a folder
        e.dataTransfer.setData("application/json", JSON.stringify({
          type: 'folder',
          id: folder.id,
          name: folder.name,
          parentId: folder.parentId
        }));
        e.dataTransfer.effectAllowed = "move";
      }
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    console.log('FolderMenuItem handleDrop called', { 
      folderId: folder.id, 
      folderName: folder.name,
      enableDragDrop,
      isItem,
      isFolder 
    });
    
    if (!enableDragDrop) {
      console.log('Drop rejected: dragDrop not enabled');
      return;
    }
    
    // Don't allow dropping on items, only on folders
    if (isItem) {
      console.log('Drop rejected: target is an item, not a folder');
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    setDragOverFolderId(null);

    try {
      // Try to get structured data first
      const dragDataJson = e.dataTransfer.getData("application/json");
      console.log('Drag data JSON:', dragDataJson);
      
      if (dragDataJson) {
        const dragData = JSON.parse(dragDataJson);
        console.log('Parsed drag data:', dragData);
        console.log('Checking conditions:', {
          isMultiSelect: dragData.isMultiSelect,
          hasOnBatchMove: !!onBatchMove,
          onBatchMoveType: typeof onBatchMove
        });
        
        if (dragData.isMultiSelect && onBatchMove) {
          // Handle multi-select drop
          const targetId = folder.id === 'root' ? null : folder.id;
          console.log('Multi-select drop detected, calling onBatchMove with targetId:', targetId);
          onBatchMove(targetId);
        } else if (dragData.isMultiSelect && !onBatchMove) {
          console.error('Multi-select drop detected but onBatchMove is not defined!');
        } else if (dragData.type === 'item' && onItemDrop) {
          // Handle item drop - only allow on folders
          if (isFolder) {
            console.log('Single item drop:', dragData.id, 'to folder:', folder.id);
            onItemDrop(dragData.id, folder.id === 'root' ? null : folder.id);
          }
        } else if (dragData.type === 'folder' && onFolderMove) {
          // Handle folder drop (move folder into this folder)
          const targetId = folder.id === 'root' ? null : folder.id;
          
          // Prevent moving a folder into itself
          if (dragData.id === folder.id) {
            console.warn('Cannot move folder into itself');
            return;
          }
          
          if (isFolder) {
            console.log('Single folder drop:', {
              sourceFolderId: dragData.id,
              sourceFolderName: dragData.name,
              targetFolderId: targetId,
              targetFolderName: folder.name
            });
            onFolderMove(dragData.id, targetId);
          }
        } else {
          console.log('No matching handler for drag data:', dragData);
        }
      } else {
        console.log('No JSON drag data, trying plain text fallback');
        // Fallback to plain text (for backward compatibility)
        const itemId = e.dataTransfer.getData("text/plain");
        if (itemId && onItemDrop && isFolder) {
          console.log('Plain text drop:', itemId);
          onItemDrop(itemId, folder.id === 'root' ? null : folder.id);
        }
      }
    } catch (error) {
      console.error('Error handling drop:', error);
      // Fallback to plain text - only allow on folders
      const itemId = e.dataTransfer.getData("text/plain");
      if (itemId && onItemDrop && isFolder) {
        onItemDrop(itemId, folder.id === 'root' ? null : folder.id);
      }
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    setContextMenu({
      isOpen: true,
      position: { x: e.clientX, y: e.clientY },
    });
  };

  const closeContextMenu = () => {
    setContextMenu({ isOpen: false, position: { x: 0, y: 0 } });
  };

  const getContextMenuItems = (): ContextMenuItem[] => {
    const items: ContextMenuItem[] = [];

    // Context menu for ITEMS (glossary items, documents, blueprints)
    if (isItem) {
      // Rename
      if (allowRename && onItemRename) {
        items.push({
          id: 'rename-item',
          label: 'Rename',
          icon: 'fa-pen',
          onClick: () => startEditing(),
        });
      }

      // Duplicate
      if (onItemDuplicate) {
        items.push({
          id: 'duplicate-item',
          label: 'Duplicate',
          icon: 'fa-copy',
          onClick: () => {
            if (folder.itemData?.id) {
              onItemDuplicate(folder.itemData.id);
            }
          },
        });
      }

      // Delete
      if (allowDelete && onItemDelete) {
        if (items.length > 0) {
          items.push({
            id: 'separator',
            label: '',
            separator: true,
            onClick: () => {},
          });
        }
        
        items.push({
          id: 'delete-item',
          label: 'Delete',
          icon: 'fa-trash',
          destructive: true,
          onClick: () => {
            if (folder.itemData?.id) {
              onItemDelete(folder.itemData.id);
            }
          },
        });
      }

      return items;
    }

    // Context menu for FOLDERS
    if (allowCreation && onFolderCreate) {
      items.push({
        id: 'create-subfolder',
        label: folder.id === 'root' ? 'Create Folder' : 'Create Subfolder',
        icon: 'fa-plus',
        onClick: () => {
          // Special case: if this is the root folder, create a top-level folder
          const parentId = folder.id === 'root' ? undefined : folder.id;
          onFolderCreate(parentId);
        },
      });
    }

    // Add item creation options based on itemType (using centralized config)
    if (allowCreation && itemType) {
      const icon = getItemTypeIcon(itemType);
      const label = `Create ${getItemTypeLabel(itemType)}`;
      
      // Map item type to creation handler
      const creationHandlers: Record<string, ((folderId?: string) => void) | undefined> = {
        'glossary': oncreateObject,
        'blueprint': onCreateBlueprint,
        'document': onCreateDocument,
        'dialog': onCreateDialog,
        'flow': onCreateFlow,
        'variable': onCreateVariable,
      };
      
      const handler = creationHandlers[itemType];
      if (handler) {
        items.push({
          id: `create-${itemType}`,
          label,
          icon,
          onClick: () => {
            // Special case: if this is the root folder, create item without folder assignment
            const folderId = folder.id === 'root' ? undefined : folder.id;
            handler(folderId);
          },
        });
      }
    }

    if (allowRename && onFolderRename) {
      items.push({
        id: 'rename',
        label: 'Rename',
        icon: 'fa-pen',
        onClick: () => startEditing(),
      });
    }

    // Add Set Color option for folders (not root)
    if (folder.id !== 'root' && onFolderColorChange) {
      items.push({
        id: 'set-color',
        label: 'Set Color',
        icon: 'fa-palette',
        onClick: () => {
          setIsColorPickerOpen(true);
          closeContextMenu();
        },
      });
    }

    if (allowDelete && onFolderDelete) {
      if (items.length > 0) {
        items.push({
          id: 'separator',
          label: '',
          separator: true,
          onClick: () => {},
        });
      }
      
      items.push({
        id: 'delete',
        label: 'Delete Folder',
        icon: 'fa-trash',
        destructive: true,
        onClick: () => onFolderDelete(folder.id),
      });
    }

    return items;
  };

  const handleColorSelect = (color: string | null) => {
    if (onFolderColorChange && folder.id !== 'root') {
      onFolderColorChange(folder.id, color);
    }
  };

  // Choose the appropriate sidebar component based on hierarchy level
  const MenuButton = isSubItem
    ? FolderSidebarMenuSubButton
    : FolderSidebarMenuButton;

  return (
    <div className="relative group">
      <MenuButton
        asChild={!isEditing}
        isActive={isSelected || isMultiSelected}
        level={level}
        isDraggedOver={isDraggedOver}
        className={cn(
          isEditing && "p-0",
          isMultiSelected && "bg-sidebar-accent/50 border-l-2 border-primary"
        )}
        draggable={enableDragDrop && !isEditing && folder.id !== 'root'}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onContextMenu={handleContextMenu}
      >
        {isEditing ? (
          <div className="flex items-center w-full px-2 py-1.5">
            <input
              ref={inputRef}
              type="text"
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              onBlur={saveEdit}
              onKeyDown={handleKeyPress}
              className="flex-1 bg-transparent border-b border-sidebar-border text-sm focus:outline-none text-sidebar-foreground"
              autoFocus
            />
          </div>
        ) : (
          <button
            onClick={(e) => {
              // Handle multi-select with Shift/Ctrl/Cmd
              if (onEntityClick && (e.shiftKey || e.ctrlKey || e.metaKey)) {
                const entity: SelectedEntity = {
                  // For items, use the actual item's ID, not the FolderItem wrapper ID
                  id: isItem && folder.itemData?.id ? folder.itemData.id : folder.id,
                  type: isItem ? 'item' : 'folder',
                  data: isItem ? folder.itemData : folder,
                };
                onEntityClick(entity, e);
                return;
              }
              
              // Normal click behavior
              if (isItem && onItemSelect) {
                // Handle item selection
                onItemSelect(folder.itemData);
              } else if (isFolder) {
                // Just select the folder, don't toggle expansion here
                // Expansion is handled by clicking the chevron
                if (folder.id === 'root') {
                  onFolderSelect(null); // null means "show all items"
                } else {
                  onFolderSelect(folder.id);
                }
              }
            }}
            className="flex items-center w-full"
          >
            {isFolder && (
              <div
                onClick={(e) => {
                  // Stop propagation to prevent folder selection
                  e.stopPropagation();
                  // Toggle expansion when clicking chevron
                  if (hasChildren && onToggleExpanded) {
                    onToggleExpanded();
                  }
                }}
                className="inline-flex items-center"
              >
                <IconProvider
                  iconstring={hasChildren ? (isExpanded ? "fa-chevron-down" : (isRTL ? "fa-chevron-left" : "fa-chevron-right")) : "fa-chevron-down"}
                  size={chevronSize}
                  style="light"
                  className={cn(
                    "me-1",
                    hasChildren 
                      ? "text-sidebar-accent-foreground cursor-pointer" 
                      : "text-sidebar-muted-foreground cursor-default opacity-50"
                  )}
                />
              </div>
            )}

            {/* Show appropriate icon based on type */}
            {isItem ? (
              <IconProvider
                iconstring={folder.icon || "fa-file"}
                style="light"
                size={folderIconSize}
                className="me-2 text-sidebar-accent-foreground"
              />
            ) : showFolderIcon && (
              <IconProvider
                iconstring="fa-folder"
                style="light"
                size={folderIconSize}
                className="me-2 text-sidebar-accent-foreground"
                color={(folder as any).color}
              />
            )}

            <span className="flex-1 text-start truncate">{folder.name}</span>

            {showItemCounts && folder.itemCount !== undefined && (
              <span className="ms-2 text-xs text-sidebar-muted-foreground">
                ({folder.itemCount})
              </span>
            )}
          </button>
        )}
      </MenuButton>

      {/* Context Menu */}
      <ContextMenu
        isOpen={contextMenu.isOpen}
        position={contextMenu.position}
        items={getContextMenuItems()}
        onClose={closeContextMenu}
      />

      {/* Color Picker Dialog */}
      <FolderColorPickerDialog
        isOpen={isColorPickerOpen}
        folderName={folder.name}
        currentColor={(folder as any).color}
        onClose={() => setIsColorPickerOpen(false)}
        onColorSelect={handleColorSelect}
      />
    </div>
  );
}
